/**
 * กิจกรรม bell in the desktop header — the same feed as the chat กิจกรรม page / phone tab
 * (docs/api/ACTIVITY.md): one unread count (chat store `activityUnread`, kept live by the
 * `notification` / `notification.read` events), one list (GET /activity), one "mark read"
 * (POST /activity/read, which every tab of the person hears). A click opens what the item is
 * about — a shared file opens its preview in ไฟล์ (url /files?file=<id>).
 * On phones the bottom กิจกรรม tab is the feed, so the bell is hidden there (activity.css).
 */
import { useEffect, useRef } from 'react';
import AlarmClock from 'lucide-react/dist/esm/icons/alarm-clock.mjs'; import AtSign from 'lucide-react/dist/esm/icons/at-sign.mjs'; import Bell from 'lucide-react/dist/esm/icons/bell.mjs'; import CheckCircle2 from 'lucide-react/dist/esm/icons/circle-check.mjs'; import ClipboardCheck from 'lucide-react/dist/esm/icons/clipboard-check.mjs'; import FileText from 'lucide-react/dist/esm/icons/file-text.mjs'; import MessageSquare from 'lucide-react/dist/esm/icons/message-square.mjs'; import PhoneMissed from 'lucide-react/dist/esm/icons/phone-missed.mjs'; import RefreshCcw from 'lucide-react/dist/esm/icons/refresh-ccw.mjs'; import Share2 from 'lucide-react/dist/esm/icons/share-2.mjs'; import Undo2 from 'lucide-react/dist/esm/icons/undo-2.mjs'; import UserPlus from 'lucide-react/dist/esm/icons/user-plus.mjs'; import XCircle from 'lucide-react/dist/esm/icons/circle-x.mjs';
import { useSession } from '../app/session.js';
import { activityApi } from '../chat/api.js';
import { relativeTime } from '../chat/format.js';
import { chatStore, useChat } from '../chat/store.js';
import type { ActivityItem } from '../chat/types.js';
import { href, navigate } from '../lib/router.js';
import { useSWR } from '../lib/swr.js';
import { AccessRequestButtons } from './accessRequestItem.js';
import { Popover } from './Popover.js';
import '../styles/activity.css';

export const ACTIVITY_ICON: Record<string, typeof Bell> = {
  'chat.mention': AtSign, 'chat.message': Bell, 'design.shared': Share2, 'file.shared': FileText, 'review.requested': ClipboardCheck,
  'review.approved': CheckCircle2, 'review.rejected': XCircle, 'workspace.added': UserPlus, 'call.missed': PhoneMissed, system: Bell,
  'letter.review_requested': ClipboardCheck, 'letter.sign_requested': ClipboardCheck, 'letter.issued': CheckCircle2, 'letter.returned': Undo2, 'letter.cancelled': XCircle, 'letter.dispatch_overdue': AlarmClock,
  'access.requested': Share2, 'access.approved': CheckCircle2, 'access.denied': XCircle,
  'approval.reminder': AlarmClock, 'approval.overdue': AlarmClock, 'approval.stale': RefreshCcw, 'approval.withdrawn': Undo2, 'approval.commented': MessageSquare,
};

export function ActivityBell() {
  const ws = useSession().workspace;
  const chat = useChat();
  const unread = chat.activityUnread;
  // Every page mounts its own header: the list is shared (lib/swr.ts), so moving between pages
  // shows it at once and does not ask the server again. The bell stays quiet when the feed is unavailable.
  const feed = useSWR<ActivityItem[]>(ws ? `activity:${ws.id}:bell` : null, () => activityApi.list(ws!.id, undefined, 20).then((r) => r.items));
  const items = feed.data ?? null;
  const setItems = (update: (xs: ActivityItem[]) => ActivityItem[]) => void feed.mutate((xs) => update(xs ?? []));
  // Reload when something arrives or is read anywhere (another tab, the กิจกรรม page).
  const epoch = useRef(chat.activityEpoch);
  useEffect(() => {
    if (epoch.current === chat.activityEpoch) return;
    epoch.current = chat.activityEpoch;
    void feed.reload();
  }, [chat.activityEpoch, feed.reload]);
  if (!ws) return null;

  const open = (i: ActivityItem, close: () => void) => {
    close();
    if (!i.readAt) {
      setItems((xs) => xs.map((x) => (x.id === i.id ? { ...x, readAt: new Date().toISOString() } : x)));
      chatStore.markActivityRead(1);
      void activityApi.read(ws.id, { ids: [i.id] }).catch(() => undefined);
    }
    navigate(i.url);
  };

  const label = unread ? `กิจกรรม (ยังไม่อ่าน ${unread})` : 'กิจกรรม';
  return (
    <Popover
      label={label}
      triggerClassName="xc-icon-btn xc-bell"
      trigger={
        <>
          <Bell aria-hidden="true" size={20} />
          {unread > 0 && (
            <span className="xc-bell-badge" aria-hidden="true">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </>
      }
    >
      {(close) => (
        <div className="xc-menu-panel xc-bell-panel">
          <div className="xc-bell-head">
            <p className="xc-menu-title">กิจกรรม</p>
            {unread > 0 && (
              <button
                type="button"
                className="xc-link-btn xc-small"
                onClick={() => {
                  setItems((xs) => xs.map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })));
                  chatStore.markActivityRead('all');
                  void activityApi.read(ws.id, { all: true }).catch(() => undefined);
                }}
              >
                อ่านทั้งหมด
              </button>
            )}
          </div>
          {items === null && <p className="xc-muted xc-small">กำลังโหลด…</p>}
          {items && items.length === 0 && <p className="xc-muted xc-small">ยังไม่มีกิจกรรม — เมื่อมีคน @ถึงคุณ แชร์งานหรือไฟล์ให้ หรือขอให้ตรวจ จะแสดงที่นี่</p>}
          {items && items.length > 0 && (
            <ul className="xc-bell-list">
              {items.map((i) => {
                const I = ACTIVITY_ICON[i.kind] ?? Bell;
                return (
                  <li key={i.id}>
                    <button type="button" className={`xc-bell-item${i.readAt ? '' : ' is-unread'}`} onClick={() => open(i, close)}>
                      <I size={18} aria-hidden="true" />
                      <span className="xc-bell-text">
                        <span>{i.title}</span>
                        {i.body && <span className="xc-bell-msg">“{i.body}”</span>}
                        <span className="xc-muted xc-small">
                          {relativeTime(i.createdAt)}
                          {i.readAt ? '' : ' · ยังไม่อ่าน'}
                        </span>
                      </span>
                    </button>
                    <AccessRequestButtons item={i} onDone={(resolved) => setItems((xs) => xs.map((x) => (x.id === i.id ? { ...x, readAt: x.readAt ?? new Date().toISOString(), payload: { ...x.payload, resolved } } : x)))} />
                  </li>
                );
              })}
            </ul>
          )}
          <a className="xc-link-btn xc-small xc-bell-all" href={href('/activity')} onClick={() => close()}>
            ดูกิจกรรมทั้งหมด
          </a>
        </div>
      )}
    </Popover>
  );
}
